import { Link } from "react-router-dom";
import { mediaUrl } from "@/lib/api";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export function Seo({ title, description, path, image, jsonLd, noindex }) {
  const full = title ? `${title} | Morphe Aesthetic` : "Morphe Aesthetic · Medicina estetica";
  const url = `${ORIGIN}${path ?? (typeof window !== "undefined" ? window.location.pathname : "")}`;
  const img = image || `${ORIGIN}/img/centro-7289.webp`;
  return (
    <>
      <title>{full}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      <meta property="og:title" content={full} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </>
  );
}

export const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-7xl px-5 sm:px-10 ${className}`}>{children}</div>
);

export function PageHero({ eyebrow, title, intro, crumbs = [], children }) {
  return (
    <section className="border-b border-[var(--line)] bg-[var(--ivory-soft)]">
      <Container className="py-16 sm:py-24">
        {crumbs.length > 0 && (
          <nav aria-label="Percorso" className="mb-8 text-sm text-[var(--muted-ink)]">
            <ol className="flex flex-wrap gap-2">
              <li><Link to="/" className="hover:text-[var(--petrol)]">Home</Link></li>
              {crumbs.map((c) => (
                <li key={c.label} className="flex gap-2">
                  <span aria-hidden="true">/</span>
                  {c.to ? <Link to={c.to} className="hover:text-[var(--petrol)]">{c.label}</Link> : <span aria-current="page">{c.label}</span>}
                </li>
              ))}
            </ol>
          </nav>
        )}
        {eyebrow && <p className="eyebrow reveal">{eyebrow}</p>}
        <h1 className="reveal reveal-1 mt-4 max-w-4xl text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">{title}</h1>
        {intro && <p className="reveal reveal-2 mt-6 max-w-2xl text-base text-[var(--muted-ink)] md:text-lg">{intro}</p>}
        {children}
      </Container>
    </section>
  );
}

export const Prose = ({ html, className = "" }) => (
  // html is sanitized server-side (nh3 allow-list) before storage
  <div className={`prose-ma ${className}`} dangerouslySetInnerHTML={{ __html: html || "" }} />
);

export function SmartImage({ mediaId, fallback, alt, className = "", width = 1200, height = 1500, eager = false, size = "lg" }) {
  const src = mediaUrl(mediaId, size) || fallback;
  if (!src) return null;
  return <img src={src} alt={alt || ""} width={width} height={height} loading={eager ? "eager" : "lazy"} decoding="async" className={className} />;
}

export const Placeholder = ({ children }) => (
  <span className="rounded bg-[var(--powder-light)] px-1.5 py-0.5 font-mono text-[0.85em] text-[var(--petrol-dark)]">{children}</span>
);

export const SectionTitle = ({ eyebrow, title, intro, className = "" }) => (
  <div className={`max-w-2xl ${className}`}>
    {eyebrow && <p className="eyebrow">{eyebrow}</p>}
    <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl">{title}</h2>
    {intro && <p className="mt-4 text-[var(--muted-ink)]">{intro}</p>}
  </div>
);
